import React, { useState, useEffect, useRef, useCallback } from 'react';
import { PageContentWrapper, PageHeader, PageDescription } from '../main/LayoutStyles';
import { StyledButton } from '../main/CommonStyles';
import {
    ExercisesGrid,
    ExerciseCardStyled,
    ExerciseIconWrapper,
    ExerciseDuration,
    NoExercisesMessage,
    TimerDisplay,
    BreathingVisualizer,
    ImmersiveExercisePlayer,
    PlayerTopBar,
    ProgressIndicator,
    MainStepArea,
    StepInstruction,
    StepNote,
    PlayerControls,
    HiddenAudioPlayer
} from './ExercisesStyles';
import { useTheme } from 'styled-components'; // <-- NOWY IMPORT
import { Zap, Wind, Headphones, Brain, Smile, PlayCircle, ChevronLeft, PauseCircle, SkipForward, CheckCircle, Info, ThumbsUp, Aperture, Ear, Users, MessageSquare, RefreshCcw } from 'lucide-react';

// Przykładowe ścieżki do audio i obrazków (zakładając, że są dostępne w folderze public/audio)
const AUDIO_PLACEHOLDERS = {
    body_scan: '/audio/body_scan_meditation.mp3',
    gratitude: '/audio/gratitude_meditation.mp3',
    safe_place: '/audio/safe_place_visualization.mp3',
    affirmations: '/audio/positive_affirmations.mp3',
    progressive_relaxation_audio: '/audio/progressive_muscle_relaxation.mp3'
};

const IMAGE_PLACEHOLDERS = {
    default: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8eW9nYSUyMHJlbGF4fGVufDB8fDB8fHww&auto=format&fit=crop&w=500&q=60',
    breath: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Nnx8bWVkaXRhdGlvbnxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=500&q=60',
    mindfulness: 'https://images.unsplash.com/photo-1474418397713-7ede21d49118?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTF8fG1pbmRmdWxuZXNzfGVufDB8fDB8fHww&auto=format&fit=crop&w=500&q=60',
    visualization: 'https://images.unsplash.com/photo-1561494241-534d71523729?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTB8fGltYWdpbmF0aW9ufGVufDB8fDB8fHww&auto=format&fit=crop&w=500&q=60'
};


const exercisesData = [
    {
        id: 'breath_calm',
        title: 'Spokojny Oddech',
        icon: <Wind size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.breath,
        category: 'Oddechowe',
        durationMinutes: 5,
        description: 'Proste ćwiczenie oddechowe pomagające uspokoić umysł i ciało. Skup się na powolnym, głębokim wdechu i wydechu.',
        steps: [
            { id: 's1', instruction: 'Usiądź wygodnie z prostym kręgosłupem lub połóż się na plecach.', durationSeconds: 15, visualState: 'normal' },
            { id: 's2', instruction: 'Jeśli chcesz, zamknij oczy lub skup wzrok w jednym punkcie przed sobą.', durationSeconds: 15, visualState: 'normal' },
            { id: 's3', instruction: 'Przez chwilę skup się na naturalnym rytmie swojego oddechu, nie zmieniając go.', durationSeconds: 60, visualState: 'normal', note: '"Normalny oddech" oznacza tu swobodny, naturalny rytm, bez świadomego kontrolowania.' },
            { id: 's4', instruction: 'Teraz, powoli wdychaj powietrze przez nos, licząc w myślach do czterech.', durationSeconds: 4, visualState: 'inhale' },
            { id: 's5', instruction: 'Delikatnie wstrzymaj oddech, licząc do czterech.', durationSeconds: 4, visualState: 'hold' },
            { id: 's6', instruction: 'Powoli i spokojnie wydychaj powietrze przez usta, licząc do sześciu.', durationSeconds: 6, visualState: 'exhale' },
            { id: 's7', instruction: 'Powtórz cykl wdech-zatrzymanie-wydech 10 razy (ok. 2 min 20 s). Skup się na płynności oddechu.', durationSeconds: (4+4+6)*10, subSteps: ['s4', 's5', 's6'], repeat: 10, isCycle: true},
            { id: 's8', instruction: 'Po zakończeniu cykli, wróć do naturalnego oddechu. Zauważ, jak się czujesz.', durationSeconds: 60, visualState: 'normal', note: '"Normalny oddech" oznacza tu swobodny, naturalny rytm, bez świadomego kontrolowania.' },
            { id: 's9', instruction: 'Kiedy będziesz gotów/gotowa, powoli otwórz oczy.', durationSeconds: 20, visualState: 'normal' },
        ],
        type: 'interactive_timer',
        details: "To klasyczne ćwiczenie oddechowe, znane również jako oddychanie pudełkowe lub kwadratowe (z modyfikacją długości wydechu), jest doskonałym sposobem na szybkie uspokojenie układu nerwowego."
    },
    {
        id: 'body_scan',
        title: 'Skanowanie Ciała',
        icon: <Aperture size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.mindfulness,
        category: 'Mindfulness',
        durationMinutes: 10,
        description: 'Medytacja polegająca na stopniowym przenoszeniu uwagi na różne części ciała, obserwując doznania bez oceniania.',
        audioUrl: AUDIO_PLACEHOLDERS.body_scan,
        type: 'audio_guided',
        details: "To ćwiczenie pomaga zwiększyć świadomość ciała i zredukować napięcie. Słuchaj uważnie instrukcji i podążaj za nimi w swoim tempie, bez oceniania pojawiających się doznań."
    },
    {
        id: 'mindful_listening',
        title: 'Uważne Słuchanie',
        icon: <Ear size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.default,
        category: 'Mindfulness',
        durationMinutes: 7,
        description: 'Ćwiczenie rozwijające uważność poprzez skupienie się na dźwiękach otoczenia, zarówno tych bliskich, jak i dalekich.',
        steps: [
            { id: 'ml1', instruction: 'Usiądź wygodnie w spokojnym miejscu i jeśli chcesz, zamknij oczy.', durationSeconds: 20 },
            { id: 'ml2', instruction: 'Skieruj swoją uwagę na dźwięki, które do Ciebie docierają. Zacznij od tych najbliższych Ciebie - może to być Twój oddech, dźwięk ubrania, bicie serca.', durationSeconds: 120 },
            { id: 'ml3', instruction: 'Stopniowo rozszerzaj pole uwagi, próbując usłyszeć dźwięki z Twojego pokoju, potem zza okna, a na końcu te najbardziej oddalone. Zauważ ich różnorodność i subtelność.', durationSeconds: 180 },
            { id: 'ml4', instruction: 'Zauważ, jak dźwięki pojawiają się i znikają w Twojej świadomości. Nie analizuj ich, nie oceniaj – po prostu je rejestruj jak obserwator. Pozwól im przychodzić i odchodzić.', durationSeconds: 120 },
            { id: 'ml5', instruction: 'Kiedy będziesz gotów/gotowa, powoli otwórz oczy.', durationSeconds: 20 },
        ],
        type: 'interactive_steps',
        details: "To ćwiczenie wyostrza zmysł słuchu i pomaga zakotwiczyć się w teraźniejszości. Praktykuj regularnie, aby zwiększyć swoją zdolność koncentracji i uważności na otaczający świat."
    },
    {
        id: 'gratitude_meditation',
        title: 'Medytacja Wdzięczności',
        icon: <Smile size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.visualization,
        category: 'Medytacja',
        durationMinutes: 8,
        description: 'Krótka medytacja prowadzona, która pomaga skupić się na pozytywnych aspektach życia i pielęgnować uczucie wdzięczności.',
        audioUrl: AUDIO_PLACEHOLDERS.gratitude,
        type: 'audio_guided',
        details: "Praktyka wdzięczności może poprawić nastrój, zwiększyć optymizm i ogólne samopoczucie. Znajdź spokojne miejsce i pozwól sobie na odczuwanie wdzięczności za małe i duże rzeczy w Twoim życiu."
    },
    {
        id: 'progressive_relaxation',
        title: 'Relaksacja Progresywna Jacobsona',
        icon: <Users size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.default,
        category: 'Relaksacja',
        durationMinutes: 15,
        description: 'Technika polegająca na napinaniu i rozluźnianiu grup mięśni w celu osiągnięcia głębokiego relaksu.',
        audioUrl: AUDIO_PLACEHOLDERS.progressive_relaxation_audio,
        steps: [
            { id: 'pr0', instruction: 'Przygotowanie: Połóż się wygodnie na plecach lub usiądź w fotelu. Zamknij oczy i weź kilka głębokich, spokojnych oddechów, pozwalając ciału zacząć się rozluźniać.', durationSeconds: 60},
            { id: 'pr1', instruction: 'Dłonie: Zaciśnij mocno obie pięści. Poczuj napięcie w dłoniach i przedramionach. Przytrzymaj 5-7 sekund. Następnie gwałtownie rozluźnij, czując jak napięcie odpływa. Skup się na uczuciu ulgi przez 15-20 sekund.', durationSeconds: 30 },
            { id: 'pr2', instruction: 'Bicepsy: Zegnij ręce w łokciach i napnij bicepsy. Przytrzymaj. Rozluźnij, pozwalając ramionom opaść swobodnie.', durationSeconds: 30 },
            { id: 'pr3', instruction: 'Czoło: Unieś brwi wysoko, marszcząc czoło. Przytrzymaj. Rozluźnij, czując jak czoło staje się gładkie.', durationSeconds: 30 },
            { id: 'pr4', instruction: 'Oczy i policzki: Zaciśnij mocno powieki i zmarszcz nos. Przytrzymaj. Rozluźnij, czując jak mięśnie wokół oczu i policzków się relaksują.', durationSeconds: 30 },
            { id: 'pr5', instruction: 'Szczęka i usta: Zaciśnij szczęki i usta. Przytrzymaj. Rozluźnij, pozwalając szczęce lekko opaść.', durationSeconds: 30 },
            { id: 'pr6', instruction: 'Szyja i kark: Delikatnie przyciśnij głowę do poduszki lub oparcia, napinając mięśnie z tyłu szyi (ostrożnie!). Przytrzymaj. Rozluźnij. Następnie pochyl głowę lekko do przodu, czując rozciąganie z tyłu szyi (bez napięcia).', durationSeconds: 45 },
            { id: 'pr7', instruction: 'Barki: Unieś barki w kierunku uszu, jakbyś chciał/a schować w nich głowę. Przytrzymaj. Opuść i rozluźnij, czując jak opadają ciężko.', durationSeconds: 30 },
            { id: 'pr8', instruction: 'Klatka piersiowa: Weź głęboki wdech, wypełniając płuca i napinając mięśnie klatki piersiowej. Wstrzymaj na chwilę. Wydychając powoli, rozluźnij.', durationSeconds: 30 },
            { id: 'pr9', instruction: 'Brzuch: Wciągnij brzuch mocno do środka, napinając mięśnie. Przytrzymaj. Rozluźnij, pozwalając brzuchowi swobodnie się unosić i opadać z oddechem.', durationSeconds: 30 },
            { id: 'pr10', instruction: 'Uda: Napnij mięśnie ud, prostując nogi. Przytrzymaj. Rozluźnij.', durationSeconds: 30 },
            { id: 'pr11', instruction: 'Łydki: Skieruj palce stóp w stronę głowy, napinając mięśnie łydek. Przytrzymaj. Rozluźnij. Następnie skieruj palce stóp w dół, napinając łydki w inny sposób. Przytrzymaj. Rozluźnij.', durationSeconds: 45 },
            { id: 'pr12', instruction: 'Stopy: Podwiń palce stóp pod siebie, napinając łuki stóp. Przytrzymaj. Rozluźnij.', durationSeconds: 30 },
            { id: 'pr13', instruction: 'Całe ciało: Teraz poczuj, jak całe Twoje ciało jest przyjemnie ciężkie i głęboko rozluźnione. Pozostań w tym stanie relaksu przez kilka minut, oddychając spokojnie i naturalnie.', durationSeconds: 180 },
            { id: 'pr14', instruction: 'Zakończenie: Powoli zacznij poruszać palcami rąk i nóg. Przeciągnij się delikatnie. Kiedy będziesz gotów/gotowa, otwórz oczy, czując się odświeżonym/odświeżoną.', durationSeconds: 60 },
        ],
        type: 'interactive_steps_with_audio_option',
        details: "Progresywna relaksacja mięśni to skuteczna metoda redukcji stresu i napięcia fizycznego. Pamiętaj, aby napinać mięśnie tylko do komfortowego poziomu, unikając bólu. Kontrast między napięciem a rozluźnieniem jest kluczowy."
    },
    {
        id: 'safe_place_visualization',
        title: 'Wizualizacja "Bezpieczne Miejsce"',
        icon: <Brain size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.visualization,
        category: 'Wizualizacja',
        durationMinutes: 12,
        description: 'Prowadzona medytacja słowna, pomagająca stworzyć w umyśle obraz spokojnego, bezpiecznego miejsca.',
        audioUrl: AUDIO_PLACEHOLDERS.safe_place,
        type: 'audio_guided',
        details: "To ćwiczenie może pomóc Ci znaleźć ukojenie, spokój i poczucie bezpieczeństwa. Wybierz ciche miejsce, gdzie nikt nie będzie Ci przeszkadzał, i pozwól swojej wyobraźni swobodnie pracować."
    },
    {
        id: 'grounding_54321',
        title: 'Ugruntowanie "5-4-3-2-1"',
        icon: <Aperture size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.mindfulness,
        category: 'Ugruntowanie',
        durationMinutes: 5,
        description: 'Technika pomagająca wrócić do chwili obecnej w przypadku silnego lęku, stresu lub natłoku myśli.',
        steps: [
            { id: 'g0', instruction: 'Zatrzymaj się na chwilę. Jeśli możesz, usiądź. Weź jeden lub dwa głębokie, uspokajające oddechy.', durationSeconds: 20 },
            { id: 'g1', instruction: '5 RZECZY, KTÓRE WIDZISZ: Rozejrzyj się wokół siebie i świadomie zauważ pięć różnych przedmiotów. Przyjrzyj im się przez chwilę, zwracając uwagę na kolor, kształt, teksturę. Nazwij je w myślach lub cicho na głos.', durationSeconds: 60, note: "Np. 'Widzę zielony kubek', 'Widzę drewniane biurko'."},
            { id: 'g2', instruction: '4 RZECZY, KTÓRE CZUJESZ: Skieruj uwagę na doznania dotykowe. Zauważ cztery rzeczy, które teraz czujesz. Może to być dotyk ubrania na skórze, stóp na podłodze, krzesła pod Tobą, powiewu powietrza.', durationSeconds: 60, note: "Np. 'Czuję miękkość swetra', 'Czuję chłód blatu'."},
            { id: 'g3', instruction: '3 DŹWIĘKI, KTÓRE SŁYSZYSZ: Wsłuchaj się uważnie i zidentyfikuj trzy różne dźwięki. Mogą być głośne lub ciche, bliskie lub dalekie.', durationSeconds: 60, note: "Np. 'Słyszę szum komputera', 'Słyszę śpiew ptaka za oknem'."},
            { id: 'g4', instruction: '2 ZAPACHY, KTÓRE CZUJESZ: Skup się na zmyśle węchu. Jakie dwa zapachy docierają do Ciebie? Jeśli nie czujesz niczego wyraźnego, spróbuj przypomnieć sobie ulubiony, przyjemny zapach.', durationSeconds: 60, note: "Np. 'Czuję zapach kawy', 'Czuję świeże powietrze'."},
            { id: 'g5', instruction: '1 RZECZ, KTÓRĄ SMAKUJESZ: Zwróć uwagę na smak w ustach. Może to być posmak ostatniego posiłku, napoju, pasty do zębów, lub po prostu naturalny smak w ustach. Możesz też napić się łyka wody i skupić na jej smaku.', durationSeconds: 60, note: "Np. 'Smakuję miętową gumę', 'Czuję neutralny smak'."},
            { id: 'g6', instruction: 'Zakończ ćwiczenie, biorąc głęboki, świadomy oddech. Zauważ, jak czujesz się teraz, bardziej obecny/obecna w tej chwili.', durationSeconds: 20 },
        ],
        type: 'interactive_steps',
        details: "Technika 5-4-3-2-1 angażuje wszystkie zmysły, pomagając odwrócić uwagę od przytłaczających myśli lub emocji i zakotwiczyć się w teraźniejszości. Możesz ją stosować dyskretnie w dowolnym miejscu i czasie."
    },
    {
        id: 'positive_affirmations_short',
        title: 'Krótkie Afirmacje Pozytywne',
        icon: <MessageSquare size={36} />,
        imageUrl: IMAGE_PLACEHOLDERS.default,
        category: 'Afirmacje',
        durationMinutes: 3,
        description: 'Sesja z odtwarzaniem serii pozytywnych stwierdzeń wspierających dobre samopoczucie i pewność siebie.',
        audioUrl: AUDIO_PLACEHOLDERS.affirmations,
        type: 'audio_guided',
        details: "Słuchaj tych afirmacji regularnie, najlepiej rano po przebudzeniu lub wieczorem przed snem, aby wzmocnić pozytywne myślenie i budować wewnętrzną siłę. Pozwól, aby słowa rezonowały w Tobie."
    }
];

const formatTime = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
        return '00:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const ActiveExerciseView = ({ exercise, onBack, onComplete }) => {
    const theme = useTheme(); // <-- POPRAWKA: Użycie hooka useTheme
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [timeLeftInStep, setTimeLeftInStep] = useState(0);
    const [isPaused, setIsPaused] = useState(true);
    const [visualBreathState, setVisualBreathState] = useState('normal');
    const [cycleCount, setCycleCount] = useState(0);
    const [exerciseStarted, setExerciseStarted] = useState(false);

    const timerRef = useRef(null);
    const audioRef = useRef(null);

    const currentStep = exercise.steps?.[currentStepIndex];
    const totalSteps = exercise.steps?.length || 0;

    const handleNextStepLogic = useCallback(() => {
        clearInterval(timerRef.current);
        if (currentStep?.isCycle && cycleCount < (currentStep.repeat || 1) - 1) {
            setCycleCount(prev => prev + 1);
            const firstSubStepIndex = exercise.steps.findIndex(s => s.id === currentStep.subSteps[0]);
            setCurrentStepIndex(firstSubStepIndex !== -1 ? firstSubStepIndex : (prevIndex => (prevIndex < totalSteps - 1 ? prevIndex + 1 : prevIndex)));
        } else if (currentStepIndex < totalSteps - 1) {
            setCurrentStepIndex(prevIndex => prevIndex + 1);
            setCycleCount(0);
        } else {
            onComplete(exercise.id);
        }
    }, [currentStep, currentStepIndex, cycleCount, exercise.id, exercise.steps, onComplete, totalSteps]);

    useEffect(() => {
        setIsPaused(true);
        clearInterval(timerRef.current);
        setCycleCount(0);
        setExerciseStarted(false);
        const stepDuration = currentStep?.durationSeconds;
        setTimeLeftInStep(typeof stepDuration === 'number' && stepDuration > 0 ? stepDuration : 0);
        setVisualBreathState(currentStep?.visualState || 'normal');

        const audioSource = currentStep?.audioUrl || exercise.audioUrl;
        if (audioSource && audioRef.current) {
            if (audioRef.current.src !== window.location.origin + audioSource) {
                audioRef.current.src = audioSource;
                audioRef.current.load();
            }
        }
    }, [currentStep, exercise.audioUrl]);

    useEffect(() => {
        if (isPaused || timeLeftInStep === 0 || !(currentStep?.durationSeconds > 0)) {
            clearInterval(timerRef.current);
            return;
        }
        timerRef.current = setInterval(() => {
            setTimeLeftInStep(prevTime => {
                if (prevTime <= 1) {
                    clearInterval(timerRef.current);
                    handleNextStepLogic();
                    return 0;
                }
                return prevTime - 1;
            });
        }, 1000);
        return () => clearInterval(timerRef.current);
    }, [isPaused, timeLeftInStep, currentStep, handleNextStepLogic]);

    const handlePlayPause = () => {
        const newPausedState = !isPaused;
        setIsPaused(newPausedState);
        const audioSource = currentStep?.audioUrl || exercise.audioUrl;
        if (audioRef.current && audioSource) {
            if (!newPausedState) {
                if (!exerciseStarted) {
                    audioRef.current.play().catch(e => console.error("Błąd autoodtwarzania audio:", e));
                } else {
                    audioRef.current.play().catch(e => console.error("Błąd odtwarzania audio:", e));
                }
            } else {
                audioRef.current.pause();
            }
        }
        if (!exerciseStarted) {
            setExerciseStarted(true);
        }
    };

    const handleUserTriggerNextStep = () => {
        handleNextStepLogic();
    };

    const visualizerDuration = currentStep?.durationSeconds || 4;

    return (
        <ImmersiveExercisePlayer imageUrl={exercise.imageUrl}>
            <PlayerTopBar>
                <StyledButton onClick={onBack} $variant="text" style={{ color: theme.colors.primaryDark, padding: theme.spacing.xs, background: 'transparent' }}>
                    <ChevronLeft size={28} />
                </StyledButton>
                <h2 className="exercise-title-active">{exercise.title}</h2>
                {totalSteps > 0 ? (
                    <ProgressIndicator>Krok {currentStepIndex + 1}/{totalSteps}</ProgressIndicator>
                ) : (
                    <div style={{ width: '80px' }} />
                )}
            </PlayerTopBar>

            {(exercise.type === 'audio_guided' || (exercise.audioUrl && exercise.type === 'interactive_steps_with_audio_option')) && (
                <HiddenAudioPlayer ref={audioRef} src={exercise.audioUrl} onEnded={() => exercise.type === 'audio_guided' ? onComplete(exercise.id) : null} preload="auto" />
            )}
            {currentStep?.audioUrl && (
                <HiddenAudioPlayer ref={audioRef} src={currentStep.audioUrl} preload="auto" />
            )}

            <MainStepArea className="custom-scrollbar">
                {currentStep?.instruction && (
                    <StepInstruction>{currentStep.instruction}
                        {currentStep?.isCycle && ` (Powtórzenie ${cycleCount + 1}/${currentStep.repeat})`}
                    </StepInstruction>
                )}

                {(exercise.type === 'interactive_timer' || exercise.type === 'interactive_steps' || exercise.type === 'interactive_steps_with_audio_option') && currentStep && currentStep.durationSeconds > 0 && (
                    <TimerDisplay>{formatTime(timeLeftInStep)}</TimerDisplay>
                )}

                {exercise.type === 'interactive_timer' && currentStep && (
                    <BreathingVisualizer className={isPaused ? 'normal' : visualBreathState} duration={isPaused ? 4 : visualizerDuration}>
                        {isPaused ? 'Pauza' : (visualBreathState !== 'normal' ? visualBreathState.charAt(0).toUpperCase() + visualBreathState.slice(1) : "Oddech")}
                    </BreathingVisualizer>
                )}

                {exercise.type === 'audio_guided' && (
                    <div style={{ textAlign: 'center', padding: theme.spacing.lg, color: theme.colors.textMedium }}>
                        <Headphones size={64} style={{ marginBottom: theme.spacing.md, color: theme.colors.primary }} />
                        <p style={{ fontSize: '1.1rem' }}>Odtwarzanie ćwiczenia prowadzonego.</p>
                        <p>Skup się na instrukcjach i pozwól sobie na relaks.</p>
                        {isPaused && !exerciseStarted && <p style={{ marginTop: theme.spacing.md, fontWeight: 'bold', fontSize: '1.2rem' }}>Naciśnij "Start", aby rozpocząć.</p>}
                    </div>
                )}
                {currentStep?.note && (
                    <StepNote>
                        <Info size={16} /> {currentStep.note}
                    </StepNote>
                )}
            </MainStepArea>

            <PlayerControls>
                { (exercise.type !== 'interactive_steps' || (currentStep?.durationSeconds > 0 || currentStep?.audioUrl || (exercise.audioUrl && exercise.type === 'interactive_steps_with_audio_option') ) ) && (
                    <StyledButton
                        onClick={handlePlayPause}
                        aria-label={isPaused ? "Odtwórz" : "Pauza"}
                    >
                        {isPaused ? <PlayCircle size={22} /> : <PauseCircle size={22} />}
                        <span style={{marginLeft: theme.spacing.xs}}>{isPaused && !exerciseStarted ? 'Start' : (isPaused ? 'Wznów' : 'Pauza')}</span>
                    </StyledButton>
                )}
                {(exercise.type === 'interactive_steps' || exercise.type === 'interactive_steps_with_audio_option') && currentStepIndex < totalSteps - 1 && (
                    <StyledButton
                        onClick={handleUserTriggerNextStep}
                        aria-label="Następny krok"
                        $variant="outline"
                    >
                        Następny <SkipForward size={20} style={{marginLeft: theme.spacing.xs}} />
                    </StyledButton>
                )}
                <StyledButton
                    onClick={() => onComplete(exercise.id)}
                    data-variant="success"
                    aria-label="Oznacz jako ukończone"
                >
                    <ThumbsUp size={20} style={{marginRight: theme.spacing.xs}} />
                    Ukończono
                </StyledButton>
            </PlayerControls>
        </ImmersiveExercisePlayer>
    );
};

const ExercisesPage = () => {
    const theme = useTheme(); // <-- POPRAWKA: Użycie hooka useTheme
    const [selectedExercise, setSelectedExercise] = useState(null);
    const [completedExercises, setCompletedExercises] = useState(() => {
        try {
            const saved = localStorage.getItem('completedExercises');
            return saved ? JSON.parse(saved) : {};
        } catch (error) {
            console.error("Error reading from localStorage", error);
            return {};
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('completedExercises', JSON.stringify(completedExercises));
        } catch (error) {
            console.error("Error writing to localStorage", error);
        }
    }, [completedExercises]);

    const handleStartExercise = (exercise) => {
        setSelectedExercise(exercise);
    };

    const handleBackToList = () => {
        setSelectedExercise(null);
    };

    const handleCompleteExercise = (exerciseId) => {
        setCompletedExercises(prev => ({ ...prev, [exerciseId]: true }));
        setSelectedExercise(null);
    };

    if (selectedExercise) {
        return (
            <PageContentWrapper className="custom-scrollbar no-padding-or-scroll-managed-internally">
                <ActiveExerciseView
                    exercise={selectedExercise}
                    onBack={handleBackToList}
                    onComplete={handleCompleteExercise}
                />
            </PageContentWrapper>
        );
    }

    return (
        <PageContentWrapper className="custom-scrollbar">
            <PageHeader>Ćwiczenia Relaksacyjne i Mindfulness</PageHeader>
            <PageDescription>
                Znajdziesz tu interaktywne ćwiczenia, które pomogą Ci się zrelaksować, zredukować stres
                i zwiększyć świadomość chwili obecnej. Wybierz ćwiczenie i rozpocznij swoją praktykę.
            </PageDescription>
            {exercisesData.length > 0 ? (
                <ExercisesGrid>
                    {exercisesData.map(exercise => (
                        <ExerciseCardStyled
                            key={exercise.id}
                            className={completedExercises[exercise.id] ? 'completed' : ''}
                            onClick={() => handleStartExercise(exercise)}
                        >
                            {exercise.imageUrl && <img src={exercise.imageUrl} alt="" className="exercise-image-background" />}

                            <div className="exercise-card-content">
                                <ExerciseIconWrapper $color={theme.colors.primaryLightest} $iconColor={theme.colors.primary}>
                                    {React.cloneElement(exercise.icon, { size: 28 })}
                                </ExerciseIconWrapper>
                                <ExerciseDuration>{exercise.category} - {exercise.durationMinutes} min</ExerciseDuration>
                                <h3>{exercise.title}</h3>
                                <p>{exercise.description}</p>

                                <StyledButton
                                    className="exercise-action-button"
                                    onClick={(e) => { e.stopPropagation(); handleStartExercise(exercise); }}
                                    aria-label={completedExercises[exercise.id] ? `Powtórz ćwiczenie: ${exercise.title}` : `Rozpocznij ćwiczenie: ${exercise.title}`}
                                >
                                    {completedExercises[exercise.id] ? (
                                        <>
                                            <RefreshCcw size={16} style={{ marginRight: theme.spacing.xs }}/> Powtórz
                                        </>
                                    ) : (
                                        <>
                                            <PlayCircle size={16} style={{ marginRight: theme.spacing.xs }}/> Rozpocznij
                                        </>
                                    )}
                                </StyledButton>
                            </div>
                            {completedExercises[exercise.id] && (
                                <div className="completed-badge" title="Ukończono">
                                    <CheckCircle size={18} />
                                </div>
                            )}
                        </ExerciseCardStyled>
                    ))}
                </ExercisesGrid>
            ) : (
                <NoExercisesMessage>
                    <Zap size={48} style={{ marginBottom: theme.spacing.md, color: theme.colors.textDisabled }} />
                    Na ten moment nie ma dostępnych żadnych ćwiczeń. Sprawdź ponownie później!
                </NoExercisesMessage>
            )}
        </PageContentWrapper>
    );
};

export default ExercisesPage;